import type { Game } from "@rally/rating";
import { useEffect, useState } from "react";
import type { MatchFormat } from "../lib/types.js";

interface Draft {
  a: string;
  b: string;
}

interface ScorePadProps {
  nameA: string;
  nameB: string;
  format: MatchFormat;
  onGamesChange: (games: Game[]) => void;
  onDone: () => void;
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "back"] as const;

/**
 * Score entry — the most-used screen in the app, and the only one that runs courtside with
 * sweaty hands. 56px targets, one game at a time, no keyboard.
 *
 * Focus advances between the two scores on its own. Games do NOT advance on their own,
 * which is a deliberate departure from docs/design-direction.md §7: `21` then `1` is a
 * decided game, so an auto-advance fires the instant someone starts typing `19` and eats
 * the second digit. One tap per game is cheaper than one wrong score per week.
 */
export function ScorePad({ nameA, nameB, format, onGamesChange, onDone }: ScorePadProps) {
  const target = format.target;
  const [drafts, setDrafts] = useState<Draft[]>([{ a: "", b: "" }]);
  const [index, setIndex] = useState(0);
  const [focus, setFocus] = useState<"a" | "b">("a");

  const current = drafts[index] ?? { a: "", b: "" };
  const games = drafts.filter(isComplete).map(toGame);

  // `onGamesChange` must be stable in the parent — it is in the dependency list, and an
  // inline arrow would re-run this on every render.
  useEffect(() => {
    onGamesChange(drafts.filter(isComplete).map(toGame));
  }, [drafts, onGamesChange]);

  const wonA = games.filter((game) => game.a > game.b).length;
  const wonB = games.filter((game) => game.b > game.a).length;

  // Clinched under the chosen format. This was hardcoded to `>= 2`, which meant a single
  // game to 30 could never be finished: the button said "Next game" forever.
  const toClinch = Math.floor(format.bestOf / 2) + 1;
  const decided = wonA >= toClinch || wonB >= toClinch;

  // Someone is ahead on games without having clinched — a retirement, or a night that ran
  // out of court time. The match is still decidable, so offer to stop here rather than
  // making people invent a game that was never played.
  const canStopEarly = !decided && wonA !== wonB;
  const currentValid = isComplete(current);

  function press(key: (typeof KEYS)[number]) {
    setDrafts((all) => {
      const next = [...all];
      const draft = { ...(next[index] ?? { a: "", b: "" }) };

      if (key === "clear") {
        draft.a = "";
        draft.b = "";
        setFocus("a");
      } else if (key === "back") {
        draft[focus] = draft[focus].slice(0, -1);
      } else {
        const grown = `${draft[focus]}${key}`.replace(/^0+(?=\d)/, "");
        if (grown.length <= 2) draft[focus] = grown;
        // Two digits is as long as a racket score gets, so move on.
        if (grown.length === 2 && focus === "a" && draft.b === "") setFocus("b");
      }

      next[index] = draft;
      return next;
    });
  }

  function nextGame() {
    if (decided) {
      onDone();
      return;
    }
    setDrafts((all) => (index === all.length - 1 ? [...all, { a: "", b: "" }] : all));
    setIndex((i) => i + 1);
    setFocus("a");
  }

  return (
    <div>
      {/* Completed games stay on screen and stay tappable. A wrong score you can see is
          a wrong score you can fix. */}
      {drafts.length > 1 || index > 0 ? (
        <div className="mb-6 flex flex-wrap gap-2">
          {drafts.map((draft, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                setIndex(i);
                setFocus("a");
              }}
              className="font-mono"
              style={{
                minHeight: 44,
                padding: "0 12px",
                borderRadius: "var(--radius-control)",
                border: `2px solid ${i === index ? "var(--color-line)" : "var(--color-line-muted)"}`,
                color: i === index ? "var(--color-ink)" : "var(--color-ink-muted)",
              }}
            >
              {draft.a || "–"}–{draft.b || "–"}
            </button>
          ))}
        </div>
      ) : null}

      <div className="micro mb-3">
        Game {index + 1}
        {format.bestOf > 1 ? ` of ${format.bestOf}` : null} · first to {target}
      </div>

      <div className="mb-5 grid grid-cols-2 gap-2.5">
        <ScoreBox
          name={nameA}
          value={current.a}
          focused={focus === "a"}
          onFocus={() => setFocus("a")}
        />
        <ScoreBox
          name={nameB}
          value={current.b}
          focused={focus === "b"}
          onFocus={() => setFocus("b")}
        />
      </div>

      <div className="mb-6 grid grid-cols-3 gap-2">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => press(key)}
            className={key === "clear" || key === "back" ? "font-semibold" : "font-display"}
            style={{
              minHeight: 62,
              fontSize: key === "clear" || key === "back" ? 14 : 32,
              fontWeight: key === "clear" || key === "back" ? 600 : 800,
              borderRadius: "var(--radius-control)",
              border: key === "clear" || key === "back" ? "none" : "2px solid var(--color-line)",
              background: key === "clear" || key === "back" ? "var(--color-surface)" : undefined,
              color: "var(--color-ink)",
            }}
            aria-label={key === "back" ? "Delete" : key === "clear" ? "Clear game" : key}
          >
            {key === "back" ? "⌫" : key === "clear" ? "Clear game" : key}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={nextGame}
        disabled={!currentValid}
        className="w-full disabled:opacity-40"
        style={{
          minHeight: 56,
          fontWeight: 700,
          borderRadius: "var(--radius-control)",
          background: "var(--color-line)",
          color: "var(--color-ground)",
        }}
      >
        {decided ? "Review match" : "Next game"}
      </button>

      {canStopEarly ? (
        <button
          type="button"
          onClick={onDone}
          className="micro mt-3 w-full underline"
          style={{ minHeight: 44 }}
        >
          Finish here — {wonA > wonB ? nameA : nameB} won {Math.max(wonA, wonB)}&ndash;
          {Math.min(wonA, wonB)}
        </button>
      ) : null}

      {/* Only once they have started. Telling someone the game is incomplete before they
          have typed anything is nagging, not helping. */}
      {!currentValid && (current.a !== "" || current.b !== "") ? (
        <p className="mt-3 text-sm text-ink-muted">
          Both scores, and no draws — someone has to have won the game.
        </p>
      ) : null}
    </div>
  );
}

function ScoreBox({
  name,
  value,
  focused,
  onFocus,
}: {
  name: string;
  value: string;
  focused: boolean;
  onFocus: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onFocus}
      className="flex min-w-0 flex-col justify-between p-3 text-left"
      aria-label={`Score for ${name}`}
      aria-current={focused}
      // The side being scored is, literally, the thing in play — so the whole panel goes on
      // the highlighter. Readable across a court with sweaty hands, which a 3px bar was not.
      style={{
        minHeight: 168,
        border: `${focused ? 3 : 2}px solid var(--color-line)`,
        background: focused ? "var(--color-highlight)" : "transparent",
        color: "var(--color-ink)",
      }}
    >
      <span className="block truncate text-sm font-semibold">{name}</span>
      <span
        className="font-display block"
        style={{
          fontSize: 104,
          fontWeight: 900,
          lineHeight: 0.8,
          color: value === "" ? "var(--color-ink-muted)" : "var(--color-ink)",
        }}
      >
        {value === "" ? "–" : value}
      </span>
    </button>
  );
}

function isComplete(draft: Draft): boolean {
  if (draft.a === "" || draft.b === "") return false;
  return Number(draft.a) !== Number(draft.b);
}

function toGame(draft: Draft): Game {
  return { a: Number(draft.a), b: Number(draft.b) };
}
